import { defineStore } from 'pinia'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { useChannelSubscription } from '../composables/useChannelSubscription'
import { apiMutate } from '../composables/apiMutate'
import { extractSnippet } from '../composables/extractSnippet'
import type { WikiPageRelationKind } from '../composables/relationKind'

export interface WikiPage {
  id: number
  path: string
  title: string | null
  category: string | null
  // Entities/ classification: 'person' | 'org' | 'product' |
  // 'project', or null for every other category (and for an entities/
  // page that hasn't been classified yet). Parsed from the page's
  // frontmatter server-side; the marker itself is stripped from
  // `content` so it never renders in the body (see WikiPageDetail.origin
  // for the analogous synthesis-provenance pattern).
  entity_type: string | null
  updated_at: string
}

// One row of the `## Relations` panel on the wiki page view. Mirrors
// the `relations` array on `GET /api/v1/wiki_pages/:id` — see
// `WikiPageSerializer#serialize_relations`. `to_id` is null when the
// target page hasn't been authored yet (legitimate per the soft FK on
// `WikiPageRelation.to_path`); the panel renders those as
// non-clickable text.
//
// `kind` is the canonical typed-edge vocabulary; the union is derived
// from `composables/relationKind.ts` so the kind list has exactly one
// TS-side source of truth.
//
// `context` is the optional one-line prose phrase
// `Wiki::BacklinkReconciler` extracts from the linker's prose for
// `mentioned-by` edges. Null on bare bullets (forward edges today,
// `mentioned-by` rows whose linker has no usable surrounding prose).
export interface WikiPageRelation {
  kind: WikiPageRelationKind
  to_path: string
  to_title: string
  to_id: number | null
  context: string | null
}

export interface WikiPageDetail extends WikiPage {
  content: string
  relations: WikiPageRelation[]
  // Provenance marker: 'synthesis' for pages the Synthesis
  // pipeline generated, null/absent otherwise. Parsed from the page's
  // frontmatter server-side; the marker itself is stripped from
  // `content` so it never renders in the body.
  origin: string | null
}

// Internal contract between `destroyPage` and its caller. Not exported
// because TypeScript infers the return type at the call site — no
// caller annotates against this name today.
//
// `status` is always "queued" today; the field exists so the SPA can
// branch later if we ever surface synchronous-vs-async deletes.
interface DeleteResult {
  path: string
  status: 'queued'
}

// Mirror of the rename endpoint's 202 body. Both paths come back so
// the SPA can show the user exactly which move was accepted without
// re-deriving the canonical (leading-slash-stripped) form client-side.
interface RenameResult {
  old_path: string
  new_path: string
  status: 'queued'
}

// `Broadcastable` (the Ruby concern) wraps the serialized record under
// a `record` key and adds an event tag. This is the wire format every
// channel that uses the concern emits — see `app/serializers/` and
// `app/models/concerns/broadcastable.rb`.
interface ChangeMessage {
  event: 'created' | 'updated' | 'destroyed'
  record: WikiPage
}

/**
 * Holds the index of wiki pages plus a live ActionCable subscription that
 * patches the store as the backend broadcasts changes.
 *
 * Uses `useAsyncResource` for the fetch / loaded / error / needsAuth
 * lifecycle and `useChannelSubscription` for the once-guarded subscribe
 * — the boilerplate stays in the composables.
 */
export const useWikiPagesStore = defineStore('wikiPages', () => {
  // useAsyncResource owns the data ref + loaded/error/needsAuth
  // flags. With `initialValue: []` the ref is typed as `Ref<WikiPage[]>`
  // (overload picks the non-null variant) so callers reading `pages`
  // don't have to null-check, and Pinia's `$patch({ pages: ... })`
  // works in tests (the returned ref IS the store's `pages` state).
  const {
    data: pages,
    loaded,
    error: loadError,
    needsAuth,
    refresh: fetchAll,
  } = useAsyncResource<WikiPage[]>(
    async () => {
      const res = await fetch('/api/v1/wiki_pages', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load wiki pages: ${res.status}`)
      return res.json()
    },
    { initialValue: [] },
  )

  // Citation hover-card preview cache. Keyed by WikiPage.id; populated
  // on first hover via `fetchSnippet`. The snippet is the first non-
  // heading paragraph of the page's body, capped at ~200 chars (see
  // composables/extractSnippet). Cache is invalidated when the page's
  // content changes — the WikiPageChannel handler clears the entry on
  // `updated`/`destroyed` events further down.
  const snippets = new Map<number, string>()
  // In-flight fetch tracking — coalesces simultaneous hovers on the
  // same marker into one network round-trip. Cleared when the promise
  // settles (success or fail).
  const snippetInflight = new Map<number, Promise<string>>()

  function applyChange(message: ChangeMessage) {
    const list = pages.value
    const idx = list.findIndex((p) => p.id === message.record.id)

    if (message.event === 'destroyed') {
      if (idx !== -1) list.splice(idx, 1)
      snippets.delete(message.record.id)
      return
    }

    // Page content may have changed — drop the cached snippet so
    // the next hover refetches. Cheap: snippet entries are short
    // strings, evicting them just means one extra GET on next
    // hover.
    if (message.event === 'updated') snippets.delete(message.record.id)

    if (idx === -1) {
      list.push(message.record)
    } else {
      list[idx] = message.record
    }
  }

  function subscribe() {
    useChannelSubscription<ChangeMessage>('WikiPageChannel', {
      received: applyChange,
      // Re-fetch on every reconnect (but not the initial connect — the
      // caller already does an explicit first-paint fetchAll) so
      // broadcasts emitted during a transient WS drop don't leave the
      // catalog stale. The Log indicator is computed from `pages.find(p
      // => p.path === 'log.md').updated_at`, so a missed `updated` event
      // for log.md is exactly what makes the unread dot stop appearing.
      // Skip-first is owned by useChannelSubscription.
      refetchOnReconnect: () => void fetchAll(),
    })
  }

  async function fetchContent(id: number): Promise<WikiPageDetail | null> {
    const res = await fetch(`/api/v1/wiki_pages/${id}`, { credentials: 'include' })
    if (res.status === 401) {
      needsAuth.value = true
      return null
    }
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`Failed to load wiki page ${id}: ${res.status}`)
    return (await res.json()) as WikiPageDetail
  }

  // First-paragraph preview for citation hover-cards. Lazy-fetches the
  // page body via `fetchContent` on miss, runs it through
  // `extractSnippet` for a one-paragraph excerpt, and caches the
  // result so subsequent hovers on the same marker don't refetch. The
  // cache is keyed by WikiPage.id and evicted when the page emits an
  // `updated` or `destroyed` broadcast (see the channel handler).
  //
  // Always resolves with a string, never throws — hovers are low-
  // stakes signal and an unhandled-rejection per failed hover would
  // flood the console without giving the user anything to act on.
  // The empty-string return covers four cases: page body is all
  // skippable blocks (no extractable prose), the page is gone (404),
  // the session is stale (401, the SPA's auth gate handles the
  // sign-out elsewhere), or the network/5xx failed transiently.
  // Transient failures aren't cached so the next hover retries.
  //
  // Coalesces concurrent in-flight requests for the same id so a
  // hover-out / hover-back-in pattern doesn't trigger a second GET.
  async function fetchSnippet(id: number): Promise<string> {
    const cached = snippets.get(id)
    if (cached !== undefined) return cached
    const inflight = snippetInflight.get(id)
    if (inflight) return inflight

    const promise = (async () => {
      try {
        const detail = await fetchContent(id)
        if (!detail) return ''
        const snippet = extractSnippet(detail.content)
        snippets.set(id, snippet)
        return snippet
      } catch {
        // Don't cache transient failures — the next hover gets a
        // clean retry. Silent because hovers shouldn't toast.
        return ''
      }
    })().finally(() => {
      snippetInflight.delete(id)
    })

    snippetInflight.set(id, promise)
    return promise
  }

  // Create + update call through the admin-gated Rails endpoints. Both
  // return the persisted detail (with freshly-derived title/category) so
  // the view can navigate straight to the new page without waiting for
  // the ActionCable broadcast to arrive.
  function createPage(input: { path: string; content: string }): Promise<WikiPageDetail> {
    return apiMutate<WikiPageDetail>('/api/v1/wiki_pages', {
      method: 'POST',
      body: { wiki_page: input },
      failureLabel: 'failed to create page',
    })
  }

  function updatePage(id: number, input: { content: string }): Promise<WikiPageDetail> {
    return apiMutate<WikiPageDetail>(`/api/v1/wiki_pages/${id}`, {
      method: 'PATCH',
      body: { wiki_page: input },
      failureLabel: 'failed to update page',
    })
  }

  // Hard-delete + reference cleanup. Mirrors `bin/brain delete <path>`.
  // The API enqueues a Solid Queue background job and returns 202
  // immediately — the actual git work + reference rewrites happen
  // asynchronously. The `WikiPageChannel`'s `destroyed` broadcast fires
  // when the job finishes; without optimistic removal the sidebar still
  // listed the doomed page for the seconds-long window between the 202
  // and the broadcast, which read as "delete didn't work". We splice
  // the row out immediately and restore it on a hard-fail so the
  // sidebar tracks the user's intent. The eventual broadcast is
  // idempotent (the destroyed handler is a no-op when idx === -1).
  async function destroyPage(id: number): Promise<DeleteResult> {
    const list = pages.value
    const idx = list.findIndex((p) => p.id === id)
    const snapshot = idx >= 0 ? list[idx] : null
    if (idx >= 0) list.splice(idx, 1)
    try {
      return await apiMutate<DeleteResult>(`/api/v1/wiki_pages/${id}`, {
        method: 'DELETE',
        failureLabel: 'failed to delete page',
      })
    } catch (e) {
      if (snapshot && !list.some((p) => p.id === id)) {
        list.splice(idx, 0, snapshot)
      }
      throw e
    }
  }

  // Path-rename + inbound-reference repointing. Mirrors
  // `bin/brain rename <old> <new>`. The API enqueues a PageRenameJob
  // and returns 202; the actual git work happens asynchronously. The
  // SPA learns about completion via the existing `WikiPageChannel`
  // broadcasts that fire when `Wiki::PageRenamer` upserts the new row
  // and refreshes the old one's tombstone fields. Unlike
  // `destroyPage`, we don't optimistically splice the old row — the
  // old path stays in the catalog as a tombstone, and the new row
  // arrives via the `created` broadcast.
  function renamePage(id: number, newPath: string): Promise<RenameResult> {
    return apiMutate<RenameResult>(`/api/v1/wiki_pages/${id}/rename`, {
      method: 'POST',
      body: { wiki_page: { new_path: newPath } },
      failureLabel: 'failed to rename page',
    })
  }

  return {
    pages,
    loaded,
    needsAuth,
    loadError,
    fetchAll,
    fetchContent,
    fetchSnippet,
    subscribe,
    createPage,
    updatePage,
    destroyPage,
    renamePage,
  }
})
